import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, X, Search, MapPin, Clock, Briefcase, Users, Globe, Check, Loader2, Share2, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import GlassBoxBadge from '@/components/GlassBoxBadge';
import ShareModal from '@/components/feed/ShareModal';

const causeAreas = [
  { value: 'all', label: 'All Causes' },
  { value: 'hunger', label: 'Hunger' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'shelter', label: 'Shelter' },
  { value: 'refugees', label: 'Refugees' },
  { value: 'children', label: 'Children' },
  { value: 'environment', label: 'Environment' },
  { value: 'disaster-response', label: 'Disaster Response' },
];

const skillFilters = [
  { value: 'all', label: 'All Skills' },
  { value: 'Teaching', label: 'Teaching' },
  { value: 'Healthcare', label: 'Medical' },
  { value: 'Construction', label: 'Construction' },
  { value: 'Logistics', label: 'Logistics' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Writing', label: 'Writing' },
  { value: 'Mentoring', label: 'Mentoring' },
];

function VolunteerCard({ opportunity, onView }) {
  const volunteerCount = Math.floor(Math.random() * 20 + 5);
  const avatarSeeds = Array.from({ length: Math.min(3, volunteerCount) }, (_, i) => opportunity.id * 3 + i + 10);
  const [showShare, setShowShare] = useState(false);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 border border-gray-100/50" data-testid={`volunteer-card-${opportunity.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-lg text-[#111827] mb-1">{opportunity.title}</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-body">{opportunity.ngoName}</span>
            <GlassBoxBadge />
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {opportunity.remote && (
            <span className="px-2.5 py-0.5 rounded-full bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium">Remote</span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setShowShare(true); }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-[#7C3AED] hover:bg-[#7C3AED]/5 transition-colors"
            data-testid={`share-volunteer-${opportunity.id}`}
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-500 font-body mb-3">
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {opportunity.location}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {opportunity.hoursPerWeek}h/week</span>
        <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {opportunity.duration}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {opportunity.skills?.map(skill => (
          <span key={skill} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-body">{skill}</span>
        ))}
      </div>
      <p className="text-sm text-[#4B5563] mb-4 line-clamp-2 leading-relaxed font-body">{opportunity.description}</p>

      {/* Social proof */}
      <div className="flex items-center justify-between mb-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {avatarSeeds.map((seed) => (
              <img key={seed} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`} alt="" className="w-5 h-5 rounded-full border-2 border-white" />
            ))}
          </div>
          <span className="text-xs text-gray-500 font-body flex items-center gap-1">
            <UserCheck className="w-3 h-3" /> {volunteerCount} volunteers
          </span>
        </div>
      </div>

      <Button onClick={() => onView(opportunity)} variant="outline" className="w-full rounded-full border-gray-200 text-sm font-body" data-testid={`learn-more-${opportunity.id}`}>
        Learn More
      </Button>

      <ShareModal open={showShare} onClose={() => setShowShare(false)} postId={`vol-${opportunity.id}`} />
    </div>
  );
}

function OpportunityDetailModal({ opportunity, open, onOpenChange, onApply }) {
  if (!opportunity) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl border-0" data-testid="opportunity-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{opportunity.title}</DialogTitle>
          <DialogDescription className="font-body">
            {opportunity.ngoName} &mdash; {opportunity.location}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-sm text-[#4B5563] leading-relaxed font-body">{opportunity.description}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 font-body">Time Commitment</p>
              <p className="text-sm font-medium text-[#111827] font-heading">{opportunity.hoursPerWeek}h/week</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 font-body">Duration</p>
              <p className="text-sm font-medium text-[#111827] font-heading">{opportunity.duration}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-body mb-1.5">Skills Needed</p>
            <div className="flex flex-wrap gap-1.5">
              {opportunity.skills?.map(s => (
                <span key={s} className="px-2.5 py-1 rounded-full bg-[#7C3AED]/10 text-[#7C3AED] text-xs font-medium">{s}</span>
              ))}
            </div>
          </div>
          {opportunity.requirements && (
            <div>
              <p className="text-xs text-gray-500 font-body mb-1">Requirements</p>
              <p className="text-sm text-[#4B5563] font-body">{opportunity.requirements}</p>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2">
            <span className="text-sm text-[#7C3AED] font-body">{opportunity.ngoName}</span>
          </div>
          <Button onClick={() => onApply(opportunity)} className="w-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full border-0 hover:shadow-lg transition-all" data-testid="apply-btn">
            Apply Now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApplyModal({ opportunity, open, onOpenChange }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); }, 1500);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => setSubmitted(false), 300);
  };

  if (!opportunity) return null;
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-2xl border-0" data-testid="apply-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {submitted ? 'Application Submitted!' : `Apply: ${opportunity.title}`}
          </DialogTitle>
          <DialogDescription className="font-body">
            {submitted ? `${opportunity.ngoName} will review your application.` : opportunity.ngoName}
          </DialogDescription>
        </DialogHeader>
        {submitted ? (
          <div className="text-center py-6" data-testid="apply-success">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#10B981]" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-[#111827] mb-2">Application submitted!</h3>
            <p className="text-sm text-gray-500 font-body">The NGO will review your application and get back to you soon.</p>
            <Button onClick={handleClose} variant="outline" className="rounded-full mt-6" data-testid="apply-done-btn">Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Name</label>
              <Input placeholder="Your full name" className="rounded-xl border-gray-200 font-body" required data-testid="apply-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Email</label>
              <Input type="email" placeholder="your@email.com" className="rounded-xl border-gray-200 font-body" required data-testid="apply-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Message</label>
              <Textarea placeholder="Tell us about your interest and relevant experience..." className="rounded-xl border-gray-200 font-body min-h-[100px]" required data-testid="apply-message" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full border-0 hover:shadow-lg transition-all" data-testid="apply-submit-btn">
              {submitting ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span> : 'Submit Application'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FilterBar({ cause, setCause, remote, setRemote, skill, setSkill, searchQuery, setSearchQuery }) {
  return (
    <div className="space-y-4 lg:space-y-0 lg:flex lg:items-center lg:gap-3 lg:flex-wrap" data-testid="volunteer-filters">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input placeholder="Search opportunities..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-xl border-gray-200 font-body" data-testid="volunteer-search" />
      </div>
      <Select value={cause} onValueChange={setCause}>
        <SelectTrigger className="w-full lg:w-[160px] rounded-xl border-gray-200" data-testid="volunteer-cause-filter">
          <SelectValue placeholder="Cause Area" />
        </SelectTrigger>
        <SelectContent>{causeAreas.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={remote} onValueChange={setRemote}>
        <SelectTrigger className="w-full lg:w-[140px] rounded-xl border-gray-200" data-testid="volunteer-remote-filter">
          <SelectValue placeholder="Work Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="remote">Remote Only</SelectItem>
          <SelectItem value="in-person">In-Person Only</SelectItem>
        </SelectContent>
      </Select>
      <Select value={skill} onValueChange={setSkill}>
        <SelectTrigger className="w-full lg:w-[140px] rounded-xl border-gray-200" data-testid="volunteer-skill-filter">
          <SelectValue placeholder="Skill" />
        </SelectTrigger>
        <SelectContent>{skillFilters.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
      </Select>
      {(cause !== 'all' || remote !== 'all' || skill !== 'all' || searchQuery) && (
        <Button variant="ghost" className="text-sm text-gray-500 hover:text-[#111827] font-body" onClick={() => { setCause('all'); setRemote('all'); setSkill('all'); setSearchQuery(''); }} data-testid="volunteer-clear-filters">
          <X className="w-4 h-4 mr-1" /> Clear
        </Button>
      )}
    </div>
  );
}

export default function VolunteerPage() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cause, setCause] = useState('all');
  const [remote, setRemote] = useState('all');
  const [skill, setSkill] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [applyOpp, setApplyOpp] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetch('/data/volunteers.json').then(r => r.json());
        setOpportunities(data);
      } catch (err) {
        toast.error('Failed to load opportunities');
      } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  let filtered = opportunities;
  if (cause !== 'all') filtered = filtered.filter(o => o.causeArea === cause);
  if (remote === 'remote') filtered = filtered.filter(o => o.remote === true);
  else if (remote === 'in-person') filtered = filtered.filter(o => o.remote === false);
  if (skill !== 'all') filtered = filtered.filter(o => o.skills?.some(s => s.toLowerCase().includes(skill.toLowerCase())));
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(o => o.title.toLowerCase().includes(q) || o.ngoName.toLowerCase().includes(q) || o.description.toLowerCase().includes(q));
  }

  const handleView = (opp) => { setSelectedOpp(opp); setDetailOpen(true); };
  const handleApply = (opp) => { setDetailOpen(false); setApplyOpp(opp); setTimeout(() => setApplyOpen(true), 200); };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="volunteer-page">
      <div className="bg-gradient-to-br from-purple-50/60 via-white to-blue-50/40 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#7C3AED] mb-3 font-body">Volunteer</p>
            <h1 className="text-4xl sm:text-5xl tracking-tighter font-semibold text-[#111827] mb-3 font-heading">Find Your Impact</h1>
            <p className="text-base lg:text-lg text-[#4B5563] max-w-2xl leading-relaxed font-body">
              Discover volunteer opportunities matched to your skills. Every hour you contribute is verified on-chain and added to your impact portfolio.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Stats bar */}
        <div className="flex flex-wrap gap-3 mb-8">
          {[
            { label: 'Active Opportunities', value: opportunities.length || '...' },
            { label: 'Volunteers Engaged', value: '340+' },
            { label: 'Hours Verified', value: '12,400+' },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 shadow-sm">
              <span className="text-sm font-bold text-[#111827] font-heading">{s.value}</span>
              <span className="text-xs text-gray-500 font-body">{s.label}</span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-8">
          <FilterBar cause={cause} setCause={setCause} remote={remote} setRemote={setRemote} skill={skill} setSkill={setSkill} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>

        <p className="text-sm text-[#4B5563] mb-6 font-body" data-testid="volunteer-count">
          {loading ? 'Loading...' : `${filtered.length} opportunities found`}
        </p>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <div className="animate-pulse space-y-3">
                  <div className="h-5 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-8 bg-gray-100 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="no-volunteer-results">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg text-[#111827] mb-2">No opportunities found</h3>
            <p className="text-sm text-[#4B5563] font-body">Try adjusting your filters to find more opportunities.</p>
          </div>
        ) : (
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
            {filtered.map((opp, i) => (
              <motion.div key={opp.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}>
                <VolunteerCard opportunity={opp} onView={handleView} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <OpportunityDetailModal opportunity={selectedOpp} open={detailOpen} onOpenChange={setDetailOpen} onApply={handleApply} />
      <ApplyModal opportunity={applyOpp} open={applyOpen} onOpenChange={setApplyOpen} />
    </div>
  );
}
